// src/views/Seguridad/Permisos/EliminarPermiso.jsx
import React from "react";
import { FaArrowLeft, FaTrashAlt } from "react-icons/fa";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson } from "../../../utils/http";

/**
 * Props:
 * - initial: { id, moduloNombre, accion }
 * - onVolver: () => void
 * - onEliminar: () => Promise<void> | void   // se llama si el backend borró con éxito
 */
export default function EliminarPermiso({
  initial,
  onVolver = () => {},
  onEliminar = () => {},
}) {
  const [removing, setRemoving] = React.useState(false);
  const [error, setError] = React.useState("");

  // Cerrar con ESC
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (!removing) onVolver();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [removing, onVolver]);

  if (!initial || !initial.id) {
    return (
      <div className="page">
        <div className="mb-4">
          <button type="button" className="btn-fil" onClick={onVolver}>
            <FaArrowLeft className="inline mr-2" /> Volver
          </button>
        </div>
        <div className="rounded-md border border-gray-200 bg-white px-4 py-4">
          <div className="text-sm text-red-700">No se encontró el permiso a eliminar.</div>
        </div>
      </div>
    );
  }

  const handleDelete = async () => {
    setError("");
    setRemoving(true);
    try {
      const url = apiRoutes.permisos.deleteUrl
        ? apiRoutes.permisos.deleteUrl(initial.id)
        : `${apiRoutes?.permisos?.base ?? ""}/permisos/${encodeURIComponent(initial.id)}`;

      await apiFetchJson(url, { method: "DELETE" }); // espera 204 sin body
      // éxito → avisamos al padre para refrescar y salir
      await onEliminar();
    } catch (e) {
      // Mensajes simples según status (nuestro controller ya manda 404/409)
      const status = e?.status ?? e?.response?.status;
      if (status === 409) {
        setError("No se puede eliminar: el permiso está asignado a uno o más roles.");
      } else if (status === 404) {
        setError("Permiso no encontrado (ya no existe).");
      } else {
        setError(e?.message || "No se pudo eliminar el permiso.");
      }
      setRemoving(false);
    }
  };

  return (
    <div className="page w-full max-w-2xl">
      {/* Header */}
      <div className="mb-4 flex items-center gap-3">
        <h2 className="page-title m-0">Eliminar Permiso</h2>
      </div>

      {/* Card */}
      <div className="rounded-md border border-gray-200 bg-white px-4 py-5">
        <div className="space-y-3">
          <p className="text-gray-800">
            Estás a punto de eliminar el siguiente permiso. Esta acción es permanente.
          </p>

          <div className="rounded border bg-gray-50 px-4 py-3 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-gray-600 min-w-28">ID:</span>
              <span className="font-semibold">{initial.id}</span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-gray-600 min-w-28">Módulo/Submódulo:</span>
              <span className="font-semibold">
                {initial.moduloNombre ?? "—"} {initial.subModuloNombre ? `→ ${initial.subModuloNombre}` : ""}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-gray-600 min-w-28">Permiso:</span>
              <span className="font-semibold">{initial.accion ?? "—"}</span>
            </div>
          </div>

          {error && <div className="text-sm text-red-600">{error}</div>}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              className="btn-fil"
              onClick={onVolver}
              disabled={removing}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn-fil inline-flex items-center gap-2"
              onClick={handleDelete}
              disabled={removing}
              title="Eliminar definitivamente"
            >
              <FaTrashAlt />
              {removing ? "Eliminando…" : "Eliminar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
